/**
 * Proporções do manequim procedural.
 *
 * No MVP não se busca realismo corporal: diferenciar proporção e escala é
 * suficiente para o aluno reconhecer adulto e criança dentro do veículo.
 * Medidas em metros.
 */
export interface ProporcoesVitima {
  id: 'adultoMasculino' | 'adultoFeminino' | 'crianca' | 'criancaPequena' | 'idoso'
  nome: string
  estatura: number
  /**
   * Massa do corpo, em quilogramas.
   *
   * Entrou quando o ocupante passou a ser corpo dinâmico na cinemática: sem ela
   * uma criança e um adulto seriam freados pelo cinto do mesmo jeito, que é
   * justamente a comparação que a aba existe para permitir. Valor típico do
   * perfil didático, como toda medida deste arquivo — não é medição.
   */
  massaKg: number
  raioCabeca: number
  alturaPescoco: number
  /** distância da base ao topo do tronco: é o eixo da coluna do manequim */
  alturaTronco: number
  larguraTronco: number
  profundidadeTronco: number
  larguraOmbros: number
  alturaPelve: number
  larguraPelve: number
  comprimentoCoxa: number
  comprimentoPerna: number
  comprimentoBraco: number
  comprimentoAntebraco: number
  raioMembro: number
}

export const ADULTO_MASCULINO: ProporcoesVitima = {
  id: 'adultoMasculino',
  nome: 'Adulto masculino',
  estatura: 1.76,
  massaKg: 78,
  raioCabeca: 0.115,
  alturaPescoco: 0.08,
  alturaTronco: 0.56,
  larguraTronco: 0.36,
  profundidadeTronco: 0.23,
  larguraOmbros: 0.44,
  alturaPelve: 0.18,
  larguraPelve: 0.34,
  comprimentoCoxa: 0.44,
  comprimentoPerna: 0.42,
  comprimentoBraco: 0.3,
  comprimentoAntebraco: 0.28,
  raioMembro: 0.058,
}

export const ADULTO_FEMININO: ProporcoesVitima = {
  id: 'adultoFeminino',
  nome: 'Adulto feminino',
  estatura: 1.64,
  massaKg: 62,
  raioCabeca: 0.107,
  alturaPescoco: 0.075,
  alturaTronco: 0.52,
  larguraTronco: 0.31,
  profundidadeTronco: 0.21,
  larguraOmbros: 0.37,
  alturaPelve: 0.17,
  larguraPelve: 0.35,
  comprimentoCoxa: 0.41,
  comprimentoPerna: 0.39,
  comprimentoBraco: 0.27,
  comprimentoAntebraco: 0.26,
  raioMembro: 0.05,
}

export const CRIANCA: ProporcoesVitima = {
  id: 'crianca',
  nome: 'Criança (~8 anos)',
  estatura: 1.28,
  massaKg: 26,
  raioCabeca: 0.098,
  alturaPescoco: 0.055,
  alturaTronco: 0.39,
  larguraTronco: 0.24,
  profundidadeTronco: 0.16,
  larguraOmbros: 0.28,
  alturaPelve: 0.13,
  larguraPelve: 0.24,
  comprimentoCoxa: 0.3,
  comprimentoPerna: 0.28,
  comprimentoBraco: 0.2,
  comprimentoAntebraco: 0.18,
  raioMembro: 0.042,
}

/**
 * Proporcionalmente menor que `CRIANCA` (~8 anos), mas com pernas longas o
 * suficiente para que `alturaPelveEmPe` continue acima da pelve sentada na
 * âncora do motorista — a mesma garantia que os demais perfis têm, para não
 * flutuar ao terminar a rota de extração de pé.
 */
export const CRIANCA_PEQUENA: ProporcoesVitima = {
  id: 'criancaPequena',
  nome: 'Criança pequena (~5 anos)',
  estatura: 1.1,
  massaKg: 18,
  raioCabeca: 0.092,
  alturaPescoco: 0.05,
  alturaTronco: 0.34,
  larguraTronco: 0.21,
  profundidadeTronco: 0.145,
  larguraOmbros: 0.25,
  alturaPelve: 0.115,
  larguraPelve: 0.21,
  comprimentoCoxa: 0.26,
  comprimentoPerna: 0.245,
  comprimentoBraco: 0.17,
  comprimentoAntebraco: 0.155,
  raioMembro: 0.038,
}

export const IDOSO: ProporcoesVitima = {
  id: 'idoso',
  nome: 'Idoso',
  estatura: 1.68,
  massaKg: 68,
  raioCabeca: 0.11,
  alturaPescoco: 0.075,
  alturaTronco: 0.53,
  larguraTronco: 0.33,
  profundidadeTronco: 0.22,
  larguraOmbros: 0.4,
  alturaPelve: 0.18,
  larguraPelve: 0.34,
  comprimentoCoxa: 0.42,
  comprimentoPerna: 0.4,
  comprimentoBraco: 0.28,
  comprimentoAntebraco: 0.27,
  raioMembro: 0.052,
}

export const PERFIS_VITIMA: Record<ProporcoesVitima['id'], ProporcoesVitima> = {
  adultoMasculino: ADULTO_MASCULINO,
  adultoFeminino: ADULTO_FEMININO,
  crianca: CRIANCA,
  criancaPequena: CRIANCA_PEQUENA,
  idoso: IDOSO,
}

export const IDS_PERFIL_VITIMA = Object.keys(PERFIS_VITIMA) as Array<ProporcoesVitima['id']>

export function perfilVitimaPorId(id: string): ProporcoesVitima {
  return PERFIS_VITIMA[id as ProporcoesVitima['id']] ?? ADULTO_MASCULINO
}

/**
 * Altura do centro da pelve com as pernas estendidas.
 * É o que impede a vítima de terminar a rota de extração flutuando.
 */
export function alturaPelveEmPe(p: ProporcoesVitima): number {
  return p.comprimentoCoxa + p.comprimentoPerna + p.alturaPelve / 2
}

/** Altura sentado, do assento ao topo da cabeça. Usada para conferir folga no habitáculo. */
export function alturaSentado(p: ProporcoesVitima): number {
  return p.alturaPelve / 2 + p.alturaTronco + p.alturaPescoco + p.raioCabeca * 2
}
